import { Icon } from "@blueprintjs/core";
import { Popover2 } from "@blueprintjs/popover2";
import { baseSearchObject, FGListen, IFGContext, useFGContext } from "nsitools-react";
import * as React from "react";
import styled from "styled-components";

import { EmailButton } from "..";
import {
  PeopleReferentialItem,
  PersonApi,
  ReferentialApi,
  ReferentialItemDto,
  SearchPeopleReferentialQuery,
} from "../../api";
import { useApiService, useTl } from "../../hooks";
import { ETLCodes } from "../../locales";
import { generateAddress } from "../../utils";
import { FGAdvancedSearchInput, IFGAdvancedSearchInputProps } from "./FGAdvancedSearchInput";

export interface IFGPeopleSearchInputProps
  extends Omit<IFGAdvancedSearchInputProps<ReferentialItemDto>, "searchFn" | "getDisplayedDataFn"> {
  isBelgian?: boolean;
  clubId?: number;
  personId?: number;
  dogId?: ((ctx: IFGContext) => number) | number;
  kennelId?: number;
  filterBreeder?: boolean;
  showWarning?: boolean;
}

export const ToolTipContainer = styled.div`
  padding: 20px;
`;

export const FGPeopleSearchInput: React.FunctionComponent<IFGPeopleSearchInputProps> = ({
  isBelgian,
  clubId,
  dogId,
  filterBreeder,
  kennelId,
  showWarning = false,
  ...fgProps
}) => {
  const refApi = useApiService(ReferentialApi);
  const personApi = useApiService(PersonApi);
  const { t } = useTl();
  const fgContext = useFGContext();
  const finalDogId = React.useMemo(() => {
    if (typeof dogId === "function") {
      return dogId(fgContext);
    } else {
      return dogId;
    }
  }, [dogId, fgContext]);

  const searchFn = React.useCallback(
    async (sObj: baseSearchObject) => {
      return refApi.searchPeople(
        SearchPeopleReferentialQuery.fromJS({
          clubId,
          isBelgian,
          dogId: finalDogId,
          filterBreeder,
          kennelId,
          sObj,
        })
      );
    },
    [clubId, finalDogId, filterBreeder, isBelgian, kennelId, refApi]
  );

  const getDisplayedDataFn = React.useCallback(
    async (id: any) => {
      const d = await refApi.searchPeople(
        new SearchPeopleReferentialQuery({
          personId: id,
          clubId,
        })
      );
      return d.results.length > 0 ? d.results[0] : null;
    },
    [clubId, refApi]
  );

  const [remark, setremark] = React.useState(null);

  const getPersonRemark = React.useCallback(
    async (value: number) => {
      if (!value) {
        setremark(null);
        return;
      }
      var result = await personApi.getPersonRemark(value);
      setremark(result);
    },
    [personApi]
  );

  return (
    <>
      <FGAdvancedSearchInput
        {...fgProps}
        searchFn={searchFn}
        getDisplayedDataFn={getDisplayedDataFn}
        showTableHeader={true}
        sortKeys={{ computedName: "ASC" }}
        rightElement={
          showWarning && !!remark ? (
            <Popover2 content={<ToolTipContainer>{remark}</ToolTipContainer>} interactionKind={"hover"}>
              <div style={{ marginLeft: "0.5rem", marginRight: "0.5rem" }}>
                <Icon icon={"warning-sign"} intent={"danger"} />
              </div>
            </Popover2>
          ) : null
        }
        columns={[
          {
            header: () => t(ETLCodes.Name),
            fieldName: "computedName",
            render: (row: PeopleReferentialItem) => row.computedName,
          },
          {
            fieldName: "email",
            header: () => t(ETLCodes.Email),
            render: (row: PeopleReferentialItem) => <EmailButton email={row.email} />,
          },
          {
            fieldName: "address",
            computed: true,
            disableSorting: true,
            header: () => t(ETLCodes.Address),
            render: (row: PeopleReferentialItem) => (
              <div> {generateAddress(row.number, row.street, row.city, row.postalCode, row.country)}</div>
            ),
          },
        ]}
      />
      <FGListen field={fgProps.name} onChanged={(value: number) => getPersonRemark(value)} />
    </>
  );
};
